import React, { useEffect, useState } from 'react';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { firebaseAuth } from '../lib/firebaseClient';

const EMAIL_KEY = 'rhwb-pulse-emailForSignIn';

const AuthCallback: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (!isSignInWithEmailLink(firebaseAuth, window.location.href)) {
      setError('This link is invalid or has already been used. Please request a new sign-in link.');
      setIsLoading(false);
      return;
    }

    const savedEmail = localStorage.getItem(EMAIL_KEY);
    if (savedEmail) {
      completeSignIn(savedEmail);
    } else {
      setIsLoading(false);
      setNeedsEmail(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const completeSignIn = async (email: string) => {
    try {
      await signInWithEmailLink(firebaseAuth, email, window.location.href);
      localStorage.removeItem(EMAIL_KEY);
      window.location.replace('/');
    } catch (err: any) {
      console.error('[AuthCallback] signInWithEmailLink error:', err);
      setError(err?.message || 'Sign-in failed. Please request a new link.');
      setIsLoading(false);
      setNeedsEmail(false);
    }
  };

  const handleEmailSubmit = () => {
    if (!emailInput.trim()) {
      setEmailError('Please enter your email address.');
      return;
    }
    setEmailError('');
    setNeedsEmail(false);
    setIsLoading(true);
    completeSignIn(emailInput.trim());
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Completing sign in...</h2>
          <p className="text-gray-600">Please wait while we verify your authentication.</p>
        </div>
      </div>
    );
  }

  if (needsEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="bg-blue-100 rounded-full p-4 mb-4 inline-block">
              <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirm Your Email</h2>
            <p className="text-gray-600 text-sm">
              To complete sign-in, please enter the email address you used to request this link.
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <label htmlFor="confirm-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="confirm-email"
                type="email"
                value={emailInput}
                onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                onKeyPress={(e) => { if (e.key === 'Enter') handleEmailSubmit(); }}
                className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your email address"
                autoFocus
              />
              {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
            </div>
            <button
              onClick={handleEmailSubmit}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
            >
              Complete Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full">
          <div className="text-center">
            <div className="bg-red-100 rounded-full p-4 mb-6 inline-block">
              <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.replace('/')}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
